import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, NavLink, Routes, Route, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, MessageSquare, Star, Plus, Trash2, BadgeCheck, Crown, Sparkles, LogOut, Search, Tag, MapPin, BarChart3, KeyRound, X, ChevronLeft, ChevronRight, FileText, Megaphone, CreditCard, Users, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import NativeSelect from "@/components/forms/NativeSelect";
import ImageUploader from "@/components/forms/ImageUploader";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { useNoIndex } from "@/hooks/use-no-index";
import ManageCategories from "@/pages/admin/ManageCategories";
import ManageAreas from "@/pages/admin/ManageAreas";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminHomeBanner from "@/pages/admin/AdminHomeBanner";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminOwners from "@/pages/admin/AdminOwners";
import AdminRazorpayLiveTest from "@/pages/admin/AdminRazorpayLiveTest";
import AdminLogin from "@/pages/AdminLogin";
import ChangePasswordDialog from "@/components/auth/ChangePasswordDialog";

const CATEGORIES = ["Luxury Spa", "Ayurvedic Spa", "Thai Spa", "Massage Center", "Deep Tissue Massage", "Couples Spa", "Wellness Spa", "Day Spa", "Reflexology Spa", "Aromatherapy Spa", "Body Massage Center", "Foot Spa", "Beauty & Spa Center"];

function Sidebar() {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const item = ({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${isActive ? "bg-[#111827] text-white" : "text-[#1F2937] hover:bg-[#F5EFE6]"}`;
  return (
    <aside className="w-full lg:w-60 lg:shrink-0">
      <div className="rounded-2xl bg-white border border-[#E7DDCF] p-3 lg:sticky lg:top-20">
        <div className="px-3 py-2">
          <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Admin</div>
          <div className="font-heading text-base">{user?.name}</div>
        </div>
        <nav className="mt-2 space-y-1">
          <NavLink to="/admin" end className={item}><LayoutDashboard size={16}/> Overview</NavLink>
          <NavLink to="/admin/analytics" className={item}><BarChart3 size={16}/> Analytics</NavLink>
          <NavLink to="/admin/spas" className={item}><Sparkles size={16}/> Spas</NavLink>
          <NavLink to="/admin/categories" className={item}><Tag size={16}/> Categories</NavLink>
          <NavLink to="/admin/areas" className={item}><MapPin size={16}/> Areas</NavLink>
          <NavLink to="/admin/leads" className={item}><MessageSquare size={16}/> Leads</NavLink>
          <NavLink to="/admin/reviews" className={item}><Star size={16}/> Reviews</NavLink>
          <NavLink to="/admin/blog" className={item}><FileText size={16}/> Blog</NavLink>
          <NavLink to="/admin/home-banner" className={item}><Megaphone size={16}/> Home Banner</NavLink>
          <NavLink to="/admin/subscriptions" className={item}><CreditCard size={16}/> Subscriptions</NavLink>
          <NavLink to="/admin/owners" className={item}><Users size={16}/> Owners</NavLink>
          <NavLink to="/admin/razorpay-test" className={item}><Zap size={16}/> Payment Test</NavLink>
        </nav>
        <div className="mt-4 space-y-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2 border-[#E7DDCF] text-[#1F2937]"
            onClick={() => setPwOpen(true)}
            data-testid="admin-change-password-button"
          >
            <KeyRound size={14}/> Change Password
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2"
            onClick={() => { logout(); nav("/"); }}
            data-testid="admin-logout-button"
          >
            <LogOut size={14}/> Logout
          </Button>
        </div>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </aside>
  );
}

function MetricCard({ c }) {
  return (
    <div className={`rounded-2xl border border-[#E7DDCF] p-4 ${c.color}`} data-testid={`metric-${c.label.toLowerCase().replace(/\W/g, '-')}`}>
      <div className={`text-xs ${c.color === "bg-[#111827] text-white" ? "text-white/70" : "text-[#4B5563]"}`}>{c.label}</div>
      <div className="font-heading text-3xl mt-1">{c.value}</div>
    </div>
  );
}

function Overview() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/metrics").then((r) => setM(r.data)); }, []);
  if (!m) return <div className="py-12 text-center">Loading metrics...</div>;
  // Spa-level cards (top row) — directly counts listings.
  const spaCards = [
    { label: "Total Spas", value: m.total_spas, color: "bg-white" },
    { label: "Free", value: m.free ?? 0, color: "bg-[rgba(31,78,61,0.08)]" },
    { label: "Featured", value: m.featured, color: "bg-[rgba(201,168,106,0.18)]" },
    { label: "Premium", value: m.premium, color: "bg-[#111827] text-white" },
    { label: "Verified", value: m.verified, color: "bg-[rgba(31,78,61,0.1)]" },
  ];
  // Owner-level cards (second row) — distinct business accounts. One owner
  // can have multiple spas, so these numbers may be lower than spaCards.
  const ownerCards = [
    { label: "Owners (total)", value: m.owners_total ?? 0, color: "bg-white" },
    { label: "Owners with Listing", value: m.owners_with_listing ?? 0, color: "bg-white" },
    { label: "Free Owners", value: m.owners_free ?? 0, color: "bg-[rgba(31,78,61,0.08)]" },
    { label: "Featured Owners", value: m.owners_featured ?? 0, color: "bg-[rgba(201,168,106,0.18)]" },
    { label: "Premium Owners", value: m.owners_premium ?? 0, color: "bg-[#111827] text-white" },
  ];
  const opsCards = [
    { label: "Leads", value: m.leads, color: "bg-white" },
    { label: "Reviews", value: m.reviews, color: "bg-white" },
    { label: "Revenue (est.)", value: `₹${(m.revenue || 0).toLocaleString()}`, color: "bg-white" },
  ];
  return (
    <div data-testid="admin-overview">
      <h2 className="font-heading text-2xl mb-5">Overview</h2>

      <div className="text-[11px] uppercase tracking-widest text-[#7A5A22] mb-2">Spas by plan</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {spaCards.map((c) => (<MetricCard key={c.label} c={c} />))}
      </div>

      <div className="text-[11px] uppercase tracking-widest text-[#7A5A22] mt-8 mb-2">Owners by plan</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {ownerCards.map((c) => (<MetricCard key={c.label} c={c} />))}
      </div>

      <div className="text-[11px] uppercase tracking-widest text-[#7A5A22] mt-8 mb-2">Engagement</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {opsCards.map((c) => (<MetricCard key={c.label} c={c} />))}
      </div>

      <div className="mt-6 text-xs text-[#4B5563] italic">Revenue is an estimate based on featured + premium plan pricing. Real revenue will activate when payments go live.</div>
    </div>
  );
}

function SpasTable() {
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [localities, setLocalities] = useState([]);
  const [dynCategories, setDynCategories] = useState(CATEGORIES);
  const [form, setForm] = useState({ spa_name: "", category: "Luxury Spa", locality: "Wakad", city: "Pune", address: "", phone: "", description: "", services: [], opening_hours: "10:00 AM - 10:00 PM", price_range: "₹₹", gallery: [], featured: false, verified: false, premium: false });

  // Re-clicking the sidebar link (same /admin/spas URL) bumps location.key —
  // use that as a signal to reset the search filter back to "show all".
  // First mount is a no-op because both values are already the initial state.
  // Debounced search: `q` (raw input) → `debouncedQ` (settled after 300ms).
  // Fetching keys off debouncedQ + page, which avoids the race condition
  // where clicking Next → setPage(2) → q-effect resets page back to 1.
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [q]);

  // When the settled query changes, snap back to page 1 so we're never stuck
  // on page 4 of a filter that only has 1 result.
  useEffect(() => { setPage(1); }, [debouncedQ]);

  // Sidebar re-click bumps location.key → reset search + page.
  useEffect(() => {
    setQ("");
    setPage(1);
  }, [location.key]);

  // Single source-of-truth fetch: fires whenever the settled query OR page
  // changes. Guaranteed to send exactly one request per state transition.
  //
  // Admin panel uses `sort=newest` so freshly-created listings surface at the
  // top for quality checks. The public /spas endpoint's default sort favours
  // featured/premium/rated spas, which is correct for shoppers but hides new
  // listings from the admin's default view.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: String(page), page_size: String(pageSize), sort: "newest" });
        if (debouncedQ) params.set("q", debouncedQ);
        const r = await api.get(`/spas?${params.toString()}`);
        if (!cancelled) {
          setItems(r.data.items);
          setTotal(r.data.total);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [page, pageSize, debouncedQ]);

  useEffect(() => {
    api.get("/meta/localities").then((r) => setLocalities(r.data.all));
    api.get("/meta/categories").then((r) => { if (r.data.categories?.length) setDynCategories(r.data.categories); });
  }, []);

  // Manually re-fetch after mutations (create/toggle/delete). Just bump the
  // fetch trigger by re-setting debouncedQ to its current value (no-op if
  // unchanged) OR by explicitly re-invoking with current params.
  const reload = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize), sort: "newest" });
    if (debouncedQ) params.set("q", debouncedQ);
    const r = await api.get(`/spas?${params.toString()}`);
    setItems(r.data.items);
    setTotal(r.data.total);
  }, [page, pageSize, debouncedQ]);

  // Reset search + go to page 1. Called by the "×" button in the search box.
  const clearSearch = () => {
    setQ("");  // debouncedQ will follow via the debounce effect (0ms when empty)
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  const toggle = async (spa, field) => {
    try {
      const r = await api.post(`/admin/spas/${spa.id}/toggle?field=${field}`);
      setItems(items.map((it) => it.id === spa.id ? { ...it, [field]: r.data.value } : it));
      toast.success(`${field} → ${r.data.value ? "on" : "off"}`);
    } catch (e) { toast.error("Toggle failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this spa? This cannot be undone.")) return;
    await api.delete(`/admin/spas/${id}`);
    setItems(items.filter((i) => i.id !== id));
    toast.success("Deleted");
  };

  const create = async () => {
    try {
      await api.post("/admin/spas", form);
      toast.success("Spa created!");
      setOpen(false); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Create failed"); }
  };

  return (
    <div data-testid="admin-spas-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading text-2xl">Spas ({total})</h2>
          <div className="text-xs text-[#4B5563] mt-0.5">Sorted by newest first</div>
        </div>
        <div className="flex gap-2">
          <div className="flex items-center gap-2 rounded-xl bg-white border border-[#E7DDCF] px-3">
            <Search size={14} className="text-[#4B5563]"/>
            <input
              placeholder="Search..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="py-2 outline-none text-sm bg-transparent min-w-[160px]"
              data-testid="admin-spas-search"
            />
            {q && (
              <button
                type="button"
                onClick={clearSearch}
                className="p-0.5 rounded hover:bg-[#F5EFE6] text-[#4B5563] hover:text-[#111827]"
                aria-label="Clear search"
                data-testid="admin-spas-search-clear"
              >
                <X size={14}/>
              </button>
            )}
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="admin-add-spa-button" className="bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white gap-2"><Plus size={14}/> Add Spa</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Create New Spa</DialogTitle></DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Name *</Label><Input value={form.spa_name} onChange={(e) => setForm({...form, spa_name: e.target.value})} data-testid="admin-create-name"/></div>
                <div><Label className="text-xs">Phone *</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="admin-create-phone"/></div>
                <div><Label className="text-xs">Category</Label>
                  <NativeSelect value={form.category} onChange={(v) => setForm({...form, category: v})} options={dynCategories} className="mt-1.5" data-testid="admin-create-category-select" />
                </div>
                <div><Label className="text-xs">Locality</Label>
                  <NativeSelect value={form.locality} onChange={(v) => setForm({...form, locality: v})} options={localities} className="mt-1.5" data-testid="admin-create-locality-select" />
                </div>
                <div className="sm:col-span-2"><Label className="text-xs">Address *</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})}/></div>
                <div className="sm:col-span-2"><Label className="text-xs">Description *</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}/></div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Cover Image &amp; Gallery</Label>
                  <ImageUploader
                    value={form.gallery || []}
                    onChange={(next) => setForm({ ...form, gallery: next, cover_image: next[0] || "" })}
                    multiple
                    maxFiles={10}
                    aspectRatio="square"
                    testId="admin-create-gallery-uploader"
                    className="mt-1.5"
                  />
                </div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-[#111827] text-white" data-testid="admin-create-submit">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="rounded-2xl bg-white border border-[#E7DDCF] overflow-hidden" data-testid="admin-spa-table">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F5EFE6] text-left">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3 hidden md:table-cell">Locality</th>
                <th className="p-3 hidden md:table-cell">Category</th>
                <th className="p-3">Flags</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-t border-[#E7DDCF] hover:bg-[#F5EFE6]/40" data-testid={`admin-spa-row-${s.slug}`}>
                  <td className="p-3"><Link to={`/spa/${s.slug}`} className="text-[#1F4E3D] hover:underline font-medium">{s.spa_name}</Link><div className="text-xs text-[#4B5563]">{s.phone}</div></td>
                  <td className="p-3 hidden md:table-cell">{s.locality}</td>
                  <td className="p-3 hidden md:table-cell">{s.category}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      <button onClick={() => toggle(s, "featured")} title="Featured" className={`p-1.5 rounded-md ${s.featured ? "bg-[#C9A86A] text-[#111827]" : "bg-[#F5EFE6] text-[#4B5563]"}`} data-testid={`toggle-featured-${s.slug}`}><Sparkles size={12}/></button>
                      <button onClick={() => toggle(s, "premium")} title="Premium" className={`p-1.5 rounded-md ${s.premium ? "bg-[#111827] text-white" : "bg-[#F5EFE6] text-[#4B5563]"}`} data-testid={`toggle-premium-${s.slug}`}><Crown size={12}/></button>
                      <button onClick={() => toggle(s, "verified")} title="Verified" className={`p-1.5 rounded-md ${s.verified ? "bg-[#1F4E3D] text-white" : "bg-[#F5EFE6] text-[#4B5563]"}`} data-testid={`toggle-verified-${s.slug}`}><BadgeCheck size={12}/></button>
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => del(s.id)} className="p-1.5 rounded-md text-[#B42318] hover:bg-red-50" data-testid={`admin-delete-spa-${s.slug}`}><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination controls — always render if we have any results so the user
          can see "showing 1-15 of 35" even when there's only one page. */}
      {total > 0 && (
        <div
          className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm"
          data-testid="admin-spas-pagination"
        >
          <div className="text-[#4B5563]" data-testid="admin-spas-page-info">
            Showing <span className="font-medium text-[#111827]">{showingFrom}</span>
            {"–"}
            <span className="font-medium text-[#111827]">{showingTo}</span>
            {" "}of <span className="font-medium text-[#111827]">{total}</span> spas
            {q && <span className="ml-1 text-[#7A5A22]">(filtered by “{q}”)</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-[#E7DDCF] gap-1"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="admin-spas-prev-page"
            >
              <ChevronLeft size={14}/> Previous
            </Button>
            <div className="text-xs text-[#4B5563] px-1" data-testid="admin-spas-page-counter">
              Page <span className="font-medium text-[#111827]">{page}</span> of <span className="font-medium text-[#111827]">{totalPages}</span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-[#E7DDCF] gap-1"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="admin-spas-next-page"
            >
              Next <ChevronRight size={14}/>
            </Button>
          </div>
        </div>
      )}
      {total === 0 && (
        <div className="mt-4 rounded-2xl bg-white border border-dashed border-[#E7DDCF] p-8 text-center text-sm text-[#4B5563]" data-testid="admin-spas-empty">
          {q
            ? <>No spas match “<span className="font-medium text-[#111827]">{q}</span>”. <button onClick={clearSearch} className="text-[#1F4E3D] font-medium hover:underline" data-testid="admin-spas-empty-clear">Clear search →</button></>
            : "No spas yet — click 'Add Spa' to create the first listing."
          }
        </div>
      )}
    </div>
  );
}

function LeadsAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/leads").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const del = async (id) => { await api.delete(`/admin/leads/${id}`); setItems(items.filter(i => i.id !== id)); toast.success("Deleted"); };
  return (
    <div data-testid="admin-leads-page">
      <h2 className="font-heading text-2xl mb-5">All Leads ({items.length})</h2>
      <div className="space-y-3">
        {items.length === 0 && <div className="py-8 text-center text-sm text-[#4B5563]">No leads yet.</div>}
        {items.map((l) => (
          <div key={l.id} className="rounded-2xl bg-white border border-[#E7DDCF] p-4">
            <div className="flex justify-between">
              <div><div className="font-medium">{l.name}</div><div className="text-xs text-[#4B5563]">For: {l.spa_name} · ☎ {l.phone} {l.email ? `· ${l.email}` : ""}</div></div>
              <div className="flex items-center gap-2"><div className="text-xs text-[#4B5563]">{new Date(l.created_at).toLocaleString()}</div><button onClick={() => del(l.id)} className="text-[#B42318] p-1"><Trash2 size={14}/></button></div>
            </div>
            <p className="text-sm text-[#1F2937] mt-2">{l.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewsAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/reviews").then((r) => setItems(r.data.items)); }, []);
  const del = async (id) => { await api.delete(`/admin/reviews/${id}`); setItems(items.filter(i => i.id !== id)); toast.success("Removed"); };
  return (
    <div data-testid="admin-reviews-page">
      <h2 className="font-heading text-2xl mb-5">All Reviews ({items.length})</h2>
      <div className="space-y-3">
        {items.length === 0 && <div className="py-8 text-center text-sm text-[#4B5563]">No reviews yet.</div>}
        {items.map((r) => (
          <div key={r.id} className="rounded-2xl bg-white border border-[#E7DDCF] p-4">
            <div className="flex justify-between">
              <div className="flex items-center gap-2"><div className="font-medium">{r.customer_name}</div><div className="flex items-center gap-1 text-sm"><Star size={14} className="fill-[#C9A86A] text-[#C9A86A]"/> {r.rating}</div></div>
              <button onClick={() => del(r.id)} className="text-[#B42318] p-1"><Trash2 size={14}/></button>
            </div>
            <p className="text-sm text-[#1F2937] mt-2">{r.review}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  useNoIndex();
  useEffect(() => {
    document.title = "Admin Dashboard - Pune Spa Guide";
    // Authenticated but not an admin → kick them home. (Owners shouldn't
    // accidentally land here even if they discover the URL.)
    if (user && user.role !== "admin") nav("/");
  }, [user, nav]);
  // Unauthenticated → render the admin login form inline on this same /admin URL.
  // This keeps the admin panel reachable via a single hidden URL (/admin) rather
  // than exposing a separate /admin/login route in the navigation.
  if (!user) return <AdminLogin />;
  if (user.role !== "admin") return null;
  return (
    <div data-testid="admin-dashboard" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col lg:flex-row gap-6">
        <Sidebar/>
        <div className="flex-1">
          <Routes>
            <Route index element={<Overview/>} />
            <Route path="analytics" element={<AdminAnalytics/>} />
            <Route path="spas" element={<SpasTable/>} />
            <Route path="categories" element={<ManageCategories/>} />
            <Route path="areas" element={<ManageAreas/>} />
            <Route path="leads" element={<LeadsAdmin/>} />
            <Route path="reviews" element={<ReviewsAdmin/>} />
            <Route path="blog" element={<AdminBlog/>} />
            <Route path="home-banner" element={<AdminHomeBanner/>} />
            <Route path="subscriptions" element={<AdminSubscriptions/>} />
            <Route path="owners" element={<AdminOwners/>} />
            <Route path="razorpay-test" element={<AdminRazorpayLiveTest/>} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
